import { memo } from 'react';
import { LoaderCircle, Sparkles } from 'lucide-react';
import { tokenize, type Token } from '@en3/shared';
import { cn } from '@/lib/cn';

/**
 * 单句渲染器 —— 阅读器里唯一"渲染成本随文章长度线性增长"的地方。
 *
 * 为什么必须用 `React.memo`：
 * 一篇 1500 词的文章大约 80–120 句、每句 15–30 个词，展开后是 **两三千个 DOM 节点**。
 * 父组件（ArticleReaderPage）会因为滚动位置、查词卡片、解析面板等状态频繁重渲染，
 * 每次重渲染都重新 `tokenize()` 整篇 + 重建两三千个元素的话，调字号、开词卡都会明显卡顿。
 * 这里只接「原始值 + 稳定回调」，浅比较就能挡住绝大多数无意义的重渲染；
 * 配合父组件用 `useCallback` 固定回调引用，滚动时整篇正文一次都不会重渲染。
 *
 * 切词一律走 `shared/lib/text.tokenize()`：后端导入落库用的是同一个函数，
 * 前端另写一套正则的话，`token_index` 会与 `article_word_senses` 对不上，
 * 「文中义」就写回到错误的词上了。
 */
export interface SentenceTextProps {
  sentenceId: string;
  /** 全文句序，用于锚点定位（进度跳转 / 长难句清单跳转） */
  globalIndex: number;
  text: string;
  isLong: boolean;
  /** 排版设置里的「高亮长难句」开关 */
  highlightLong: boolean;
  /** 本句是否是当前查词卡片所对应的句子（给一点定位反馈） */
  active: boolean;
  /** 本句正在请求解析（按钮转圈） */
  analyzing: boolean;
  onWordClick: (sentenceId: string, sentenceText: string, token: Token) => void;
  onAnalyze: (sentenceId: string, globalIndex: number) => void;
}

export const SentenceText = memo(function SentenceText({
  sentenceId,
  globalIndex,
  text,
  isLong,
  highlightLong,
  active,
  analyzing,
  onWordClick,
  onAnalyze,
}: SentenceTextProps) {
  const tokens = tokenize(text);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  for (const token of tokens) {
    // 词与词之间的空格、标点原样保留成文本节点：丢掉它们会把句子拼成一坨，
    // 而"标点也算可点区域"会让用户误点出标点符号的词典查询。
    if (token.charStart > cursor) nodes.push(text.slice(cursor, token.charStart));
    nodes.push(
      <button
        key={`w-${token.index}`}
        type="button"
        onClick={() => onWordClick(sentenceId, text, token)}
        className="rounded-[3px] text-left hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
      >
        {token.surface}
      </button>,
    );
    cursor = token.charEnd;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));

  const longStyle = isLong && highlightLong;

  return (
    <span
      id={`s-${globalIndex}`}
      data-global-index={globalIndex}
      className={cn(
        'rounded-sm',
        // 长难句只做"轻微"高亮：整句铺底色 + 下划线会盖住正文的重点，
        // 而且在暗色主题下底色一重就变成"反白"，反而更难读。
        longStyle && 'bg-accent/40 underline decoration-primary/40 decoration-2 underline-offset-4',
        active && 'bg-accent',
      )}
    >
      {nodes}
      {isLong && (
        <button
          type="button"
          onClick={() => onAnalyze(sentenceId, globalIndex)}
          disabled={analyzing}
          aria-label="解析这个长难句"
          className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-primary/30 px-1.5 align-middle text-[11px] leading-5 text-primary hover:bg-accent disabled:opacity-60"
        >
          {analyzing ? (
            <LoaderCircle aria-hidden className="size-3 animate-spin" />
          ) : (
            <Sparkles aria-hidden className="size-3" />
          )}
          解析
        </button>
      )}
    </span>
  );
});
